import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../layouts/AppShell";
import { GET, POST, PUT, loading, alert, format_date } from "../components/CustomComponent";
import { getBirthdayInfo, BirthdayDecor, BirthdayBadge, BIRTHDAY_HERO_BACKGROUND, BIRTHDAY_HERO_SHADOW, BIRTHDAY_GLOW } from "../components/birthday";

const getProfile = () => {
  try {
    const rawProfile = localStorage.getItem("ptn-profile");
    return rawProfile ? JSON.parse(rawProfile) : null;
  } catch (error) {
    return null;
  }
};

const getThailandDate = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
};

const formatDisplayDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return format_date(date, "dd/mm/yyyy hh:mm");
};

const formatDisplayDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return format_date(date, "dd/mm/yyyy");
};

const formatDisplayTime = (value) => {
  if (!value) return "--:--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const [hours = "--", minutes = "--"] = String(value).split(":");
    return `${hours}:${minutes}`;
  }

  return date.toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatWorkHours = (value) => {
  if (value === undefined || value === null || value === "") return "-";

  const totalHours = Number(value);
  if (Number.isNaN(totalHours)) return "-";

  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);

  if (hours === 0) return `${minutes} นาที`;
  if (minutes === 0) return `${hours} ชม.`;
  return `${hours} ชม. ${minutes} นาที`;
};

const getCheckInWindowStart = (referenceDate, workingStartTime) => {
  if (!referenceDate || !workingStartTime) return null;

  const [hours, minutes] = String(workingStartTime)
    .split(":")
    .map((value) => Number(value));

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  const windowStart = new Date(referenceDate);
  windowStart.setHours(hours, minutes, 0, 0);
  windowStart.setHours(windowStart.getHours() - 1);

  return windowStart;
};

const isBeforeWorkingStart = (checkInDate, workingStartTime) => {
  if (!checkInDate || !workingStartTime) return false;

  const [hours, minutes] = String(workingStartTime)
    .split(":")
    .map((value) => Number(value));

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;

  const workingStart = new Date(checkInDate);
  workingStart.setHours(hours, minutes, 0, 0);

  return checkInDate.getTime() < workingStart.getTime();
};

const getStatusLabel = (status) => {
  if (status === "present" || status === "late") {
    return "เข้างานแล้ว";
  }

  if (status === "absent" || status === "leave" || status === "holiday") {
    return "ออกงานแล้ว";
  }

  return "ยังไม่เช็กชื่อ";
};

const DEFAULT_HERO_BACKGROUND =
  "radial-gradient(circle at top right, rgba(47, 213, 228, 0.24), transparent 26%), linear-gradient(135deg, #08233d 0%, #0d4d83 52%, #11a4bf 100%)";

const EmployeeWelcome = () => {
  const profile = useMemo(() => getProfile(), []);
  const employee = profile?.employee || null;
  const [attendance, setAttendance] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const employeeName = employee?.first_name;
  const workingStartTime = attendance?.working_start_time || employee?.working_start_time || null;
  const workingEndTime = attendance?.working_end_time || employee?.working_end_time || null;
  const workingTimeRange =
    workingStartTime && workingEndTime
      ? `${formatDisplayTime(workingStartTime)} - ${formatDisplayTime(workingEndTime)}`
      : "-";

  const today = getThailandDate();
  const { isBirthday, birthdayAge, birthdayWish } = getBirthdayInfo(profile, today);
  const checkInWindowStart = getCheckInWindowStart(currentTime, workingStartTime);
  const canCheckInNow = !workingStartTime || !checkInWindowStart || currentTime.getTime() >= checkInWindowStart.getTime();

  const loadCurrentAttendance = async (showLoader = true) => {
    if (!employee?.uid) return;

    if (showLoader) {
      loading(true);
    }

    const openAttendanceResult = await GET("v1/internal/attendance", {
      employee_id: employee.uid,
      open_only: true,
      page: 1,
      page_size: 1,
    });

    if (openAttendanceResult?.status && openAttendanceResult?.data?.length) {
      setAttendance(openAttendanceResult.data[0]);
    } else {
      const todayAttendanceResult = await GET("v1/internal/attendance", {
        employee_id: employee.uid,
        attendance_date: today,
        page: 1,
        page_size: 1,
      });

      if (todayAttendanceResult?.status) {
        setAttendance(todayAttendanceResult?.data?.[0] || null);
      } else {
        setAttendance(null);
      }
    }

    if (showLoader) {
      loading(false);
    }
  };

  useEffect(() => {
    if (profile?.user_type === "employee") {
      loadCurrentAttendance();
    }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  const handleCheckIn = async () => {
    if (!employee?.uid) {
      await alert("", "warning", "แจ้งเตือน", "ไม่พบข้อมูลพนักงาน");
      return;
    }

    if (!canCheckInNow) {
      await alert(
        "",
        "warning",
        "แจ้งเตือน",
        `สามารถเข้างานได้ล่วงหน้าไม่เกิน 1 ชั่วโมง ก่อนเวลา ${formatDisplayTime(workingStartTime)}`
      );
      return;
    }

    if (!(await alert("", "info", "ยืนยัน", "ยืนยันการเข้างาน?", "ยืนยัน", "ยกเลิก"))) {
      return;
    }

    loading(true);
    const nowDate = new Date();
    const now = nowDate.toISOString();
    const result = await POST("v1/internal/attendance", {
      employee_id: employee.uid,
      attendance_date: today,
      check_in_at: now,
      status: "present",
    });

    if (result?.status) {
      const complimentMessage = isBeforeWorkingStart(nowDate, workingStartTime)
        ? "เข้างานเรียบร้อยแล้ว ขอบคุณที่มาถึงก่อนเวลาในวันนี้"
        : "เข้างานเรียบร้อยแล้ว";
      await loadCurrentAttendance(false);
      loading(false);
      await alert("", "success", "สำเร็จ", complimentMessage);
    } else {
      loading(false);
      await alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถเข้างานได้");
    }
  };

  const handleCheckOut = async () => {
    if (!attendance?.uid) {
      await alert("", "warning", "แจ้งเตือน", "ยังไม่พบรายการเข้างานที่ยังไม่ออกงาน");
      return;
    }

    if (attendance?.check_out_at) {
      await alert("", "warning", "แจ้งเตือน", "มีการออกงานแล้ว");
      return;
    }

    if (!(await alert("", "info", "ยืนยัน", "ยืนยันการออกงาน?", "ยืนยัน", "ยกเลิก"))) {
      return;
    }

    loading(true);
    const now = new Date().toISOString();
    const result = await PUT(`v1/internal/attendance/${attendance.uid}`, {
      check_out_at: now,
    });

    if (result?.status) {
      await loadCurrentAttendance(false);
      loading(false);
      await alert("", "success", "สำเร็จ", "ออกงานเรียบร้อยแล้ว");
    } else {
      loading(false);
      await alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถออกงานได้");
    }
  };

  const isCrossDayAttendance =
    attendance?.attendance_date && attendance.attendance_date !== today && !attendance?.check_out_at;
  const attendanceReferenceLabel = isCrossDayAttendance
    ? `รอบเข้างานวันที่ ${formatDisplayDate(attendance?.attendance_date)}`
    : "ช่วงเวลาทำงาน";

  return (
    <div className="d-flex flex-column gap-4">
      <div
        className="rounded-4 rounded-xl-5 overflow-hidden position-relative p-4 p-md-5"
        style={{
          background: isBirthday ? BIRTHDAY_HERO_BACKGROUND : DEFAULT_HERO_BACKGROUND,
          boxShadow: isBirthday ? BIRTHDAY_HERO_SHADOW : "0 28px 60px rgba(8, 35, 61, 0.22)",
        }}
      >
        <div
          className="position-absolute rounded-circle"
          style={{
            top: -70,
            right: -40,
            width: 240,
            height: 240,
            background: "radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 72%)",
          }}
        />
        <div
          className="position-absolute rounded-circle"
          style={{
            bottom: -90,
            left: -30,
            width: 280,
            height: 280,
            background: isBirthday
              ? BIRTHDAY_GLOW
              : "radial-gradient(circle, rgba(88, 229, 255, 0.20) 0%, rgba(88, 229, 255, 0) 72%)",
          }}
        />

        {isBirthday ? <BirthdayDecor /> : null}

        <div className="position-relative row g-4 align-items-center text-white">
          <div className="col-lg-7">

            {isBirthday ? (
              <>
                <BirthdayBadge birthdayAge={birthdayAge} className="mb-3" />

                <h1 className="fw-bold mb-2" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", textShadow: "0 6px 20px rgba(120, 30, 70, 0.25)" }}>
                  🎉 Happy Birthday, คุณ {employeeName}
                </h1>

                <p className="mb-4" style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)", color: "rgba(255,255,255,0.94)", maxWidth: 560 }}>
                  {birthdayWish}
                </p>
              </>
            ) : (
              <h1 className="fw-bold mb-3" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)" }}>
                สวัสดี, คุณ {employeeName}
              </h1>
            )}

            <div
              className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4"
              style={{
                width: "fit-content",
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "rgba(238, 248, 255, 0.92)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
              }}
            >
              <span className="fw-semibold">ช่วงเวลาทำงานของคุณ</span>
              <span style={{ opacity: 0.72 }}>•</span>
              <span>{workingTimeRange}</span>
            </div>

            <div className="d-flex flex-wrap gap-3 welcome-actions">
              <button
                type="button"
                className="btn px-4 py-3 fw-bold"
                onClick={handleCheckIn}
                disabled={!!attendance?.check_in_at || !canCheckInNow}
                style={{
                  minWidth: 220,
                  borderRadius: 18,
                  color: "#0c3d63",
                  border: "none",
                  background: attendance?.check_in_at || !canCheckInNow
                    ? "rgba(255,255,255,0.45)"
                    : "linear-gradient(135deg, #f6fbff 0%, #bdf5ff 100%)",
                  boxShadow: "0 16px 30px rgba(3, 27, 46, 0.16)",
                }}
              >
                {attendance?.check_in_at ? "เข้างานแล้ว" : "เข้างาน"}
              </button>

              <button
                type="button"
                className="btn px-4 py-3 fw-bold text-white"
                onClick={handleCheckOut}
                disabled={!attendance?.check_in_at || !!attendance?.check_out_at}
                style={{
                  minWidth: 220,
                  borderRadius: 18,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background:
                    !attendance?.check_in_at || attendance?.check_out_at
                      ? "rgba(255,255,255,0.10)"
                      : "linear-gradient(135deg, rgba(12, 117, 230, 0.92) 0%, rgba(18, 208, 223, 0.92) 100%)",
                  boxShadow: "0 16px 30px rgba(3, 27, 46, 0.12)",
                }}
              >
                {attendance?.check_out_at ? "ออกงานแล้ว" : "ออกงาน"}
              </button>
            </div>
          </div>

          <div className="col-lg-5">
            <div
              className="rounded-4 p-4 p-md-4 position-relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.16), 0 18px 36px rgba(3, 27, 46, 0.16)",
              }}
            >
              <div
                className="position-absolute rounded-circle"
                style={{
                  top: -48,
                  right: -42,
                  width: 160,
                  height: 160,
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 72%)",
                }}
              />
              <div
                className="position-absolute rounded-circle"
                style={{
                  bottom: -56,
                  left: -28,
                  width: 140,
                  height: 140,
                  background:
                    "radial-gradient(circle, rgba(115, 243, 255, 0.16) 0%, rgba(115, 243, 255, 0) 72%)",
                }}
              />

              <div className="position-relative">
                <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <div
                      className="small text-uppercase fw-bold mb-2"
                      style={{
                        letterSpacing: "0.16em",
                        color: "rgba(224,244,255,0.74)",
                      }}
                    >
                      เวลา ณ ปัจจุบัน
                    </div>
                    <div
                      className="small"
                      style={{ color: "rgba(238, 248, 255, 0.74)" }}
                    >
                      Bangkok Time
                    </div>
                  </div>

                  <div
                    className="px-3 py-2 rounded-pill small fw-semibold"
                    style={{
                      color: "#e9fbff",
                      background: "rgba(255,255,255,0.10)",
                      border: "1px solid rgba(255,255,255,0.14)",
                    }}
                  >
                    GMT+7
                  </div>
                </div>

                <div
                  className="fw-bold mb-3"
                  style={{
                    fontSize: "clamp(2.6rem, 4vw, 3.7rem)",
                    lineHeight: 0.95,
                    letterSpacing: "0.04em",
                    color: "#f7fdff",
                    textShadow: "0 6px 18px rgba(3, 27, 46, 0.22)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {currentTime.toLocaleTimeString("th-TH", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </div>

                <div
                  className="rounded-4 px-3 py-3"
                  style={{
                    background: "rgba(5, 26, 43, 0.18)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    color: "rgba(238, 248, 255, 0.92)",
                  }}
                >
                  {currentTime.toLocaleDateString("th-TH", { weekday: "long" })} {format_date(currentTime, "dd/mm/yyyy")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div
            className="h-100 rounded-4 p-4"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #f4fbff 100%)",
              border: "1px solid rgba(18, 125, 255, 0.10)",
              boxShadow: "0 16px 32px rgba(12, 118, 229, 0.07)",
            }}
          >
            <div className="small text-uppercase text-secondary mb-2">สถานะการลงเวลา</div>
            <div className="fw-bold mb-2" style={{ fontSize: "1.4rem", color: "#103756" }}>
              {attendance ? getStatusLabel(attendance.status) : "ยังไม่เข้างาน"}
            </div>
            <div className="text-secondary mt-2">
              {attendanceReferenceLabel}: <span className="fw-semibold text-dark">{workingTimeRange}</span>
            </div>
            {!attendance?.check_in_at && !canCheckInNow && workingStartTime ? (
              <div className="text-secondary mt-2">
                เริ่มเข้างานได้ตั้งแต่ <span className="fw-semibold text-dark">{formatDisplayTime(checkInWindowStart)}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div
            className="h-100 rounded-4 p-4"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #f4fbff 100%)",
              border: "1px solid rgba(18, 125, 255, 0.10)",
              boxShadow: "0 16px 32px rgba(12, 118, 229, 0.07)",
            }}
          >
            <div className="small text-uppercase text-secondary mb-2">เวลาเข้างาน / ออกงาน</div>
            <div className="d-flex justify-content-between align-items-end">
              <div>
                <div className="small text-secondary">เข้า</div>
                <div className="fw-bold" style={{ fontSize: "1.6rem", color: "#0e4f80" }}>
                  {formatDisplayTime(attendance?.check_in_at)}
                </div>
              </div>
              <div className="text-secondary fw-semibold">ถึง</div>
              <div className="text-end">
                <div className="small text-secondary">ออก</div>
                <div className="fw-bold" style={{ fontSize: "1.6rem", color: "#0e4f80" }}>
                  {formatDisplayTime(attendance?.check_out_at)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div
            className="h-100 rounded-4 p-4"
            style={{
              background: "linear-gradient(180deg, #ffffff 0%, #f4fbff 100%)",
              border: "1px solid rgba(18, 125, 255, 0.10)",
              boxShadow: "0 16px 32px rgba(12, 118, 229, 0.07)",
            }}
          >
            <div className="small text-uppercase text-secondary mb-2">ชั่วโมงทำงาน</div>
            <div className="fw-bold mb-2" style={{ fontSize: "1.8rem", color: "#103756" }}>
              {formatWorkHours(attendance?.actual_working_hours)}
            </div>
            <div className="text-secondary">
              {attendance?.check_out_at
                ? "สรุปจากเวลาเข้าและเวลาออกของรอบงานนี้"
                : "จะแสดงผลหลังจากออกงาน"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DefaultWelcome = () => {
  const profile = useMemo(() => getProfile(), []);
  const { isBirthday, birthdayAge, birthdayWish, birthdayName } = getBirthdayInfo(profile, getThailandDate());

  return (
    <div
      className="rounded-4 rounded-xl-5 overflow-hidden position-relative p-4 p-md-5"
      style={{
        minHeight: "calc(100vh - 180px)",
        background: isBirthday
          ? BIRTHDAY_HERO_BACKGROUND
          : "radial-gradient(circle at top right, rgba(47, 213, 228, 0.22), transparent 26%), linear-gradient(135deg, #08233d 0%, #0d4d83 55%, #12a7bf 100%)",
        boxShadow: isBirthday ? BIRTHDAY_HERO_SHADOW : "0 24px 60px rgba(8, 35, 61, 0.20)",
      }}
    >
      <div
        className="position-absolute rounded-circle"
        style={{
          top: -60,
          right: -40,
          width: 220,
          height: 220,
          background: "radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 72%)",
          filter: "blur(4px)",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          bottom: -80,
          left: -20,
          width: 260,
          height: 260,
          background: isBirthday
            ? BIRTHDAY_GLOW
            : "radial-gradient(circle, rgba(88, 229, 255, 0.20) 0%, rgba(88, 229, 255, 0) 72%)",
        }}
      />

      {isBirthday ? <BirthdayDecor /> : null}

      <div className="position-relative text-white d-flex flex-column justify-content-center h-100">
        <div
          className="px-3 py-2 rounded-pill d-inline-flex align-items-center gap-2 mb-4"
          style={{
            width: "fit-content",
            background: isBirthday ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.12)",
            border: `1px solid ${isBirthday ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.16)"}`,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            fontWeight: isBirthday ? 600 : 400,
          }}
        >
          {isBirthday ? (
            <>
              <span style={{ fontSize: "1.1rem" }}>🎂</span>
              <span>วันนี้เป็นวันเกิดของคุณ{birthdayAge ? ` · ครบ ${birthdayAge} ปี` : ""}</span>
            </>
          ) : (
            "ParthenonTech Internal Platform"
          )}
        </div>

        <h1
          className="fw-bold mb-3"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.6rem)",
            maxWidth: 760,
            ...(isBirthday ? { textShadow: "0 6px 20px rgba(120, 30, 70, 0.25)" } : {}),
          }}
        >
          {isBirthday ? `🎉 Happy Birthday${birthdayName ? `, คุณ ${birthdayName}` : ""}` : "ยินดีต้อนรับสู่ระบบ"}
        </h1>
        <p
          className="mb-4"
          style={{
            color: isBirthday ? "rgba(255,255,255,0.94)" : "rgba(238, 248, 255, 0.86)",
            fontSize: "1.05rem",
            lineHeight: 1.8,
          }}
        >
          {isBirthday
            ? birthdayWish
            : "จัดการงานบุคลากร การเงิน เอกสาร และข้อมูลสำคัญขององค์กรได้ในที่เดียว พร้อมเริ่มต้นใช้งานจากแดชบอร์ดหลักของคุณ"}
        </p>
      </div>
    </div>
  );
};

export default function Welcome() {
  const profile = getProfile();
  const isEmployee = profile?.user_type === "employee";

  return (
    <AppShell
      title={isEmployee ? "เข้างาน" : "Welcome"}
      description={isEmployee ? "ลงเวลาเข้าออกงานประจำวัน" : "ยินดีต้อนรับสู่ระบบ ParthenonTech"}
    >
      {isEmployee ? <EmployeeWelcome /> : <DefaultWelcome />}
    </AppShell>
  );
}
